import React, { useState } from 'react';
import { createReminder, dismissReminder } from '../api/reminders.js';

/**
 * ReminderForm — step-by-step reminder creation.
 * Flow: idle → text input + "Далее" → date picker + "Создать"
 */
export function ReminderForm({ leadId, reminders = [], onSaved }) {
  const [step, setStep] = useState('idle');
  const [text, setText] = useState('');
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setError(null);
    if (!date) {
      setError('Выберите дату и время');
      return;
    }
    const remindAt = new Date(date).toISOString();
    if (new Date(remindAt) <= new Date()) {
      setError('Дата должна быть в будущем');
      return;
    }
    setSaving(true);
    try {
      await createReminder({ leadId, text: text.trim(), remindAt });
      setText('');
      setDate('');
      setStep('idle');
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = async (id) => {
    try {
      await dismissReminder(id);
      onSaved?.();
    } catch (err) {
      console.error('Failed to dismiss reminder', err);
    }
  };

  const handleCancel = () => {
    setStep('idle');
    setText('');
    setDate('');
    setError(null);
  };

  return (
    <div className='reminder-form'>
      <div className='reminder-form__header'>
        <span className='reminder-form__title'>Напоминания</span>
        {step === 'idle' && (
          <button
            type='button'
            className='ghost-button ghost-button--xs'
            onClick={() => setStep('text')}
          >
            Добавить
          </button>
        )}
      </div>

      {step === 'text' && (
        <div className='reminder-form__step'>
          <input
            type='text'
            className='reminder-form__input'
            placeholder='Введите текст напоминания'
            value={text}
            onChange={e => setText(e.target.value)}
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter' && text.trim()) setStep('datetime'); }}
          />
          <div className='reminder-form__step-actions'>
            <button
              type='button'
              className='primary-button primary-button--sm'
              onClick={() => {
                if (text.trim()) {
                  setError(null);
                  setStep('datetime');
                } else {
                  setError('Введите текст');
                }
              }}
              disabled={!text.trim()}
            >
              Далее
            </button>
            <button type='button' className='ghost-button ghost-button--xs' onClick={handleCancel}>
              Отмена
            </button>
          </div>
          {error && <div className='reminder-form__error'>{error}</div>}
        </div>
      )}

      {step === 'datetime' && (
        <div className='reminder-form__step'>
          <div className='reminder-form__step-preview'>"{text}"</div>
          <input
            type='datetime-local'
            className='reminder-form__input'
            value={date}
            onChange={e => setDate(e.target.value)}
            autoFocus
          />
          <div className='reminder-form__step-actions'>
            <button
              type='button'
              className='primary-button primary-button--sm'
              onClick={handleSave}
              disabled={saving || !date}
            >
              {saving ? 'Сохранение...' : 'Создать'}
            </button>
            <button type='button' className='ghost-button ghost-button--xs' onClick={() => setStep('text')}>
              Назад
            </button>
          </div>
          {error && <div className='reminder-form__error'>{error}</div>}
        </div>
      )}

      {reminders.length > 0 && (
        <ul className='reminder-form__list'>
          {reminders.map(reminder => (
            <li key={reminder.id} className='reminder-form__item'>
              <div className='reminder-form__item-info'>
                <span className='reminder-form__item-text'>{reminder.text}</span>
                <span className='reminder-form__item-date'>
                  {new Date(reminder.remindAt).toLocaleString('ru-RU')}
                </span>
              </div>
              <button
                type='button'
                className='ghost-button ghost-button--xs ghost-button--danger'
                onClick={() => handleDismiss(reminder.id)}
              >
                Убрать
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
